import { useState, useEffect } from "react";
import { AboutUsEntry } from "../Types/Types";

const apiUrl = process.env.REACT_APP_API_URL;
const lang = localStorage.getItem("lang") || "en";
const baseUrl = `${apiUrl}/api/about`;

const useAboutUs = () => {
  const [success, setSuccess] = useState<string | null>(null);

  const [aboutUsData, setAboutUsData] = useState<AboutUsEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("position");
  const [sortOrder, setSortOrder] = useState<string>("asc");

  const fetchAboutUs = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");
    const headers: HeadersInit = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const encodedSearchQuery = encodeURIComponent(searchQuery);

    try {
      const response = await fetch(

        `${baseUrl}?search=${encodedSearchQuery}&sortBy=${sortBy}&sortOrder=${sortOrder}&lang=${lang}`,
        {
          method: "GET",
          headers,
        }
      );

      const data = await response.json();

      if (response.ok) {
        setAboutUsData(data.aboutUsEntries);
      } else {
        setError("Failed to fetch About Us entries.");
      }
    } catch (err) {
      setError("An error occurred while fetching entries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAboutUs();
  }, [searchQuery, sortBy, sortOrder]);

  const createAboutUs = async (formData: FormData) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${baseUrl}`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to create About Us entry.");
      }

      setSuccess("About Us created successfully!");  // <--- Success message

      await fetchAboutUs();
    } catch (err: any) {
      setError(err.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };


  const updateAboutUs = async (id: string, formData: FormData) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${baseUrl}/${id}`, {
        method: "PUT",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to update dosages");
      }

      setSuccess("About Us update successfully!");  // <--- Success message

      await fetchAboutUs(); // Refresh list to reflect updated product
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };




  const deleteAboutUs = async (entryId: string) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${baseUrl}/${entryId}?lang=${lang}`, {
        method: "DELETE",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      if (!response.ok) {
        setError("Failed to delete About Us entry.");
        return;
      }

      setAboutUsData((prev) => prev.filter((entry) => entry._id !== entryId));
      setError("Delete About Us successfully.");


      await fetchAboutUs(); // Refresh list to reflect updated product

    } catch (err: any) {
      setError(err.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const reorder = async (newOrder: { _id: string; position: number }[]) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${baseUrl}/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(newOrder),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reorder history');
      }

      // Update local state to reflect the new order
      setAboutUsData(prevHistory => {
        // Sort local history by the new positions from newOrder
        const orderMap = new Map(newOrder.map(item => [item._id, item.position]));
        return [...prevHistory].sort((a, b) => (orderMap.get(a._id) ?? 0) - (orderMap.get(b._id) ?? 0));
      });
      await fetchAboutUs();
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred while reordering');
      return false;
    } finally {
      setLoading(false);
    }
  };


  return {
    aboutUsData,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    createAboutUs,
    updateAboutUs,
    deleteAboutUs,
    fetchAboutUs,
    reorder,
    success,
    setSuccess
  };
};

export default useAboutUs;
